/**
 * Canonical safety incident type vocabulary.
 *
 * Single source of truth for the frontend, in the same spirit as `roles.ts`:
 * import from here instead of re-declaring a TYPE_LABELS map (or, worse,
 * deriving a label from a boolean check) in individual files.
 *
 * A NEAR MISS IS NOT AN INCIDENT
 * ------------------------------
 * The two are separate reports with separate front doors: "Report Incident"
 * (`IncidentReportForm`) and "Report Near Miss" (the modal on Safety &
 * Compliance). They share a storage table — a near miss is persisted as a row
 * whose type is `near_miss` — which is why `near_miss` still has to be
 * *renderable*. It is not *reportable*: the incident form must never offer it,
 * or the same event gets filed through two doors and lands on the safety
 * record as an injury-class incident.
 *
 * Hence two exports, and the distinction is the whole point of the file:
 *
 *   INCIDENT_TYPE_LABELS   — every type that can come back from the API.
 *                            Display only. Exhaustive.
 *   REPORTABLE_INCIDENT_TYPES — what "Report Incident" is allowed to offer.
 *                            Deliberately a subset.
 */

/** Every incident type the backend can return on a stored row. */
export type IncidentType =
  | 'near_miss'
  | 'minor_injury'
  | 'major_injury'
  | 'environmental'
  | 'property_damage'
  | 'equipment_failure'
  /**
   * The ONLY type MVIR can draw a numerator from. A vehicle incident filed as
   * `property_damage` is invisible to the motor vehicle rate — which is why
   * this is offered in the incident form rather than left to the API alone.
   */
  | 'motor_vehicle';

/**
 * Human-readable labels keyed by IncidentType.
 *
 * `Record<IncidentType, string>` (not Partial) on purpose: adding a value to
 * IncidentType must fail the build here rather than silently rendering a raw
 * `equipment_failure` somewhere in the UI.
 */
export const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  near_miss: 'Near Miss',
  minor_injury: 'Minor Injury',
  major_injury: 'Major Injury',
  environmental: 'Environmental',
  property_damage: 'Property Damage',
  equipment_failure: 'Equipment Failure',
  motor_vehicle: 'Motor Vehicle',
};

/**
 * The types "Report Incident" offers, in display order.
 *
 * Not derived from INCIDENT_TYPE_LABELS — the subset IS the rule. `near_miss`
 * is excluded because it has its own report; `equipment_failure` is excluded
 * because it is raised by the inspection flow, not typed in by hand.
 *
 * `motor_vehicle` IS reportable and must stay that way. It is the sole
 * numerator MVIR can draw from, so leaving it out of this list would not merely
 * hide an option — it would guarantee the rate reads 0.00 forever while crews
 * file every collision as property damage.
 */
export const REPORTABLE_INCIDENT_TYPES: IncidentType[] = [
  'minor_injury',
  'major_injury',
  'environmental',
  'property_damage',
  'motor_vehicle',
];

/** The type that reveals the vehicle fields. Compare against this, never a label. */
export const MOTOR_VEHICLE_TYPE: IncidentType = 'motor_vehicle';

/** The stored type of a near miss row. Compare against this, never a label. */
export const NEAR_MISS_TYPE: IncidentType = 'near_miss';

/**
 * Look up an incident type label with a fallback to the raw value.
 * Unknown values fall back to the raw string — never to another type's label,
 * which is the failure mode that made a dispatcher render as "Field".
 */
export function getIncidentTypeLabel(type: string | undefined | null): string {
  if (!type) return '—';
  return (INCIDENT_TYPE_LABELS as Record<string, string>)[type] || type;
}

/**
 * Is this row a near miss rather than an incident?
 *
 * Reads either casing — `/safety/incidents` answers camelCase and
 * `/safety/near-misses` answers snake_case, and both shapes reach the same
 * components.
 */
export function isNearMissRecord(record: any): boolean {
  return (record?.incidentType ?? record?.incident_type) === NEAR_MISS_TYPE;
}

/**
 * The incident rows that count as incidents.
 *
 * `/safety/incidents` returns near misses mixed in with real incidents, so any
 * figure derived from that list — a count, a "days since", a recent-activity
 * line — has to filter first or it silently counts near misses as injuries.
 */
/**
 * The severity a `safety_incidents` row actually carries, or null if it carries
 * none.
 *
 * ── THE COLUMN IS `severity_level`, AND IT IS SPELLED THREE WAYS ────────────
 * `GET /safety/near-misses` is raw SQL — `SELECT si.*, u.first_name … FROM
 * safety_incidents si` — with no field mapping, so it answers in the relation's
 * own snake_case. `POST /safety/near-misses` answers a drizzle row and is
 * camelCase. And `severity` is not a column on `safety_incidents` at all; it is the
 * name the report FORM's local state uses, which is how it came to be read off
 * an API row that has never had it.
 *
 * Returns null rather than a default: an absent severity is not a low one, and
 * the caller decides whether that means no pill or a placeholder.
 *
 * Two other sites still hand-roll this read — `IncidentDetailModal` and
 * `SafetyRecordsScreen`. Both are correct today because both spell all three;
 * they are the next call sites to move here, not defects.
 */
export function readIncidentSeverity(record: any): string | null {
  if (!record) return null;
  return record.severityLevel || record.severity_level || record.severity || null;
}

// ─── Recordable classification ───────────────────────────────────────────────
/**
 * OH&S recordability, mirroring the API's `ALL_RECORDABLE_CLASSES` and the
 * CHECK constraint migration 0312 puts on `safety_incidents.recordable_class`.
 * Sending a value outside this set is a 23514 the UI cannot explain.
 *
 * ── NULL IS NOT 'not_recordable' ─────────────────────────────────────────────
 *   null             → nobody has classified this yet. Counts toward nothing.
 *   'not_recordable' → a human reviewed it and found it non-recordable.
 *
 * Two different facts. Collapsing them lets an unreviewed backlog read as a
 * clean safety record, which is why the form's default is an unselected option
 * that submits `undefined` rather than a pre-picked 'not_recordable'.
 */
export type RecordableClass =
  | 'not_recordable'
  | 'first_aid'
  | 'medical_aid'
  | 'restricted_work'
  | 'lost_time'
  | 'fatality';

/** Exhaustive. A class with no label would render its raw code on the record. */
export const RECORDABLE_CLASS_LABELS: Record<RecordableClass, string> = {
  not_recordable: 'Not recordable',
  first_aid: 'First aid',
  medical_aid: 'Medical aid',
  restricted_work: 'Restricted work',
  lost_time: 'Lost time',
  fatality: 'Fatality',
};

/** Dropdown order — increasing severity. Display only; nothing stores the index. */
export const RECORDABLE_CLASS_OPTIONS: RecordableClass[] = [
  'not_recordable',
  'first_aid',
  'medical_aid',
  'restricted_work',
  'lost_time',
  'fatality',
];

/**
 * The classes that count toward TRIF.
 *
 * `first_aid` is deliberately absent: first aid only is the textbook
 * non-recordable injury, and counting it would inflate TRIF against every peer
 * company's figure on a prequalification package.
 */
export const RECORDABLE_CLASSES: RecordableClass[] = [
  'medical_aid',
  'restricted_work',
  'lost_time',
  'fatality',
];

/** The classes for which days away / days restricted are meaningful to collect. */
export const LOST_DAY_CLASSES: RecordableClass[] = ['restricted_work', 'lost_time'];

export function getRecordableClassLabel(value: string | undefined | null): string {
  if (!value) return 'Not yet classified';
  return (RECORDABLE_CLASS_LABELS as Record<string, string>)[value] || value;
}

// ─── Preventability ──────────────────────────────────────────────────────────
/**
 * Three states, and the third is not a synonym for the second.
 *
 * `null` → not yet reviewed. `false` → reviewed, judged not preventable.
 * ONLY `true` counts toward MVIR. The API stores this with `?? null` rather
 * than `|| null` for the same reason: `||` turns a reviewed "no" back into
 * "never looked at it" and silently drops the incident from the review queue.
 */
export const PREVENTABLE_OPTIONS: { value: boolean | null; label: string }[] = [
  { value: null, label: 'Not yet reviewed' },
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];
